// CTV-653 / CTV-775: the collapsed-by-default disclosure that fronts the
// Waymark panel in both surfaces (wizard creative step and Edit commercial
// tab). CTV-775 rebuilt it to the chosen composite ("B with C's thumbnails",
// docs/design/ctv775-banner-options.html): a station-gradient row with a
// slow continuous shimmer, a segmented four-pill stage indicator with the
// stage word and time estimate (the ONLY progress element; the old bottom
// bar duplicated it and was dropped in the ledger review), a poster
// thumbnail at left once the render lands (single in ready, stacked in
// committed), and a white "banner action pill" on the collapsed row
// whenever the buyer has something to click (Review / Run this
// commercial / Make another commercial).
//
// Deliberate non-behaviors, unchanged from CTV-653:
//   - Expanding never triggers work. Generation is already running (the
//     surface's kickoff started it) and Make another is a click, never an
//     auto-start.
//   - Nothing auto-expands, not even render completion. `expanded` is plain
//     local state; the live teaser + pill do the enticing.
//
// The pill PERFORMS its action (opens the editor, commits, starts another)
// and is a sibling of the expand control, never nested inside it (row = div
// with an inner button for expand + the pill button). Clicking anywhere else
// on the row expands.

/* eslint-disable react-refresh/only-export-components -- deriveDisclosureState is exported for the spec beside the component, matching WaymarkProvider */
import React from 'react';
import { ChevronDown, Pencil, Play, Plus, Sparkles, Loader2 } from 'lucide-react';
import { useWaymarkOptional } from './context';
import PrimaryButton from '../shared/PrimaryButton';
import { useCreativePreviewUrl } from '../shared/useCreativePreviewUrl';
import type { ReceiptEntry } from './types';
import { tintFromAccent } from '../steps/_shared';
import * as analytics from '../../../lib/analytics';
import { EVENTS } from '../../../lib/analytics';

export type DisclosureState =
  | 'idle'
  | 'analyzing'
  | 'drafting'
  | 'customize'
  | 'finalizing'
  | 'ready'
  | 'failed'
  | 'committed';

interface WaymarkDisclosureProps {
  accentColor: string;
  primaryColor?: string;
  // The station slug, for signed poster URLs of the receipt's slots.
  slug: string;
  // CTV-785: the finished AI commercials on the surface (receipt.ts), for
  // the stacked posters and the count chip. Order = creation order.
  receipt: ReceiptEntry[];
  // True when at least one commercial exists and every one has been in the
  // campaign at some point (useAiCommercials.allSettled). Gates Make another.
  allSettled: boolean;
  // True when the mounted commercial is itself committed (the buyer chose
  // Customize on a committed slot). The row then describes the campaign, not
  // that commercial's ready state.
  activeCommitted?: boolean;
  onCustomize?: () => void;
  onRun?: () => void;
  onMakeAnother?: () => void;
  isCommitting?: boolean;
  isMakingAnother?: boolean;
  children: React.ReactNode;
}

const STYLE_ID = 'ctv-waymark-disclosure-styles';
const CSS = `
@keyframes ctvWmSheen { 0% { transform: translateX(-100%); } 60% { transform: translateX(100%); } 100% { transform: translateX(100%); } }
@keyframes ctvWmPulse { 0%,100% { opacity: .35; } 50% { opacity: 1; } }
@keyframes ctvWmGlow { 0% { box-shadow: 0 0 0 0 var(--wm-glow); } 100% { box-shadow: 0 0 0 14px transparent; } }
.ctv-wm-row { transition: transform .18s ease, box-shadow .18s ease, filter .18s ease; }
.ctv-wm-row:hover { transform: translateY(-1px); filter: brightness(1.06); box-shadow: 0 16px 34px -14px var(--wm-primary); }
.ctv-wm-row[data-expanded="true"]:hover { transform: none; }
.ctv-wm-row .ctv-wm-sweep { animation: ctvWmSheen 6s ease-in-out infinite; }
.ctv-wm-row[data-glow="true"] { animation: ctvWmGlow 1.4s ease-out 1; }
.ctv-wm-row .ctv-wm-seg[data-state="active"] { animation: ctvWmPulse 1.2s ease-in-out infinite; }
.ctv-wm-expand { display: grid; grid-template-rows: 1fr; animation: ctvWmExpand .28s cubic-bezier(.2,.8,.2,1); }
.ctv-wm-expand > .ctv-wm-expand-clip { min-height: 0; overflow: hidden; }
.ctv-wm-expand[data-settled="true"] > .ctv-wm-expand-clip { overflow: visible; }
.ctv-wm-expand[data-closing="true"] { animation: ctvWmCollapse .22s ease forwards; }
@keyframes ctvWmExpand { from { grid-template-rows: 0fr; opacity: 0; } to { grid-template-rows: 1fr; opacity: 1; } }
@keyframes ctvWmCollapse { from { grid-template-rows: 1fr; opacity: 1; } to { grid-template-rows: 0fr; opacity: 0; } }
@media (prefers-reduced-motion: reduce) {
  .ctv-wm-row, .ctv-wm-row .ctv-wm-sweep, .ctv-wm-row .ctv-wm-seg, .ctv-wm-expand { animation: none !important; transition: none !important; }
  .ctv-wm-row:hover { transform: none; }
}
`;

// CTV-785: how long the collapse keyframes run; the body unmounts just after.
const COLLAPSE_MS = 220;

function ensureStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

// JS mirror of the media query so the animated nodes are not rendered at all
// under reduced motion (the CSS block above is the belt; this is the braces).
function useReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

// The four stages the buyer walks through: analyze -> draft -> customize ->
// final render. `stage` is the 1-based stage the segmented indicator marks
// as current (null = no indicator), `pulse` animates that segment during
// wait stages, `label` is the stage word beside the segments and `estimate`
// the time hint kept from the panel's old strip.
const STAGE: Record<
  DisclosureState,
  { pulse: boolean; stage: 1 | 2 | 3 | 4 | null; label: string | null; estimate: string | null; done?: boolean }
> = {
  idle: { pulse: false, stage: null, label: null, estimate: null },
  analyzing: { pulse: true, stage: 1, label: 'Analyzing', estimate: '~30s' },
  drafting: { pulse: true, stage: 2, label: 'Drafting', estimate: '~4 min' },
  customize: { pulse: false, stage: 3, label: 'You customize', estimate: null },
  finalizing: { pulse: true, stage: 4, label: 'Uploading', estimate: '~3 min' },
  ready: { pulse: false, stage: 4, label: 'Ready', estimate: null, done: true },
  failed: { pulse: false, stage: null, label: null, estimate: null },
  committed: { pulse: false, stage: null, label: null, estimate: null },
};

export interface DisclosureInputs {
  /** At least one commercial exists and every one has been in the campaign at some point. */
  allSettled: boolean;
  /** The mounted (active) commercial is itself committed (the render right after Run). */
  activeCommitted: boolean;
  /** Finished commercials on the surface (in the campaign or not). */
  finishedCount: number;
}

/**
 * Pure state derivation, exported for the spec. `ctx` is the mounted
 * provider's context (null when no commercial is active on the surface).
 * The row describes the ACTIVE commercial while one is uncommitted; once the
 * active one is committed (or none is active) it describes the campaign.
 */
export function deriveDisclosureState(
  ctx: {
    status: string;
    isFinalizing: boolean;
    renderedUrl: string | null;
    partnerUnavailable: boolean;
  } | null,
  inputs: DisclosureInputs,
): DisclosureState {
  if (ctx?.isFinalizing) return 'finalizing';
  if (!ctx || inputs.activeCommitted) {
    return inputs.finishedCount > 0 ? 'committed' : 'idle';
  }
  if (ctx.partnerUnavailable) return 'failed';
  switch (ctx.status) {
    case 'topic_searching':
      return 'analyzing';
    case 'generating':
      return 'drafting';
    case 'topic_failed':
    case 'generation_failed':
      return 'failed';
    case 'ready':
      return ctx.renderedUrl ? 'ready' : 'customize';
    default:
      return 'idle';
  }
}

const WaymarkDisclosure: React.FC<WaymarkDisclosureProps> = ({
  accentColor,
  primaryColor,
  slug,
  receipt,
  allSettled,
  activeCommitted = false,
  onCustomize,
  onRun,
  onMakeAnother,
  isCommitting = false,
  isMakingAnother = false,
  children,
}) => {
  ensureStyles();
  const waymark = useWaymarkOptional();
  const reducedMotion = useReducedMotion();
  const [expanded, setExpanded] = React.useState(false);
  // CTV-785: the expanded body unfolds (grid rows 0fr to 1fr) instead of
  // popping in. It mounts on expand, plays the collapse keyframes on close,
  // and unmounts after them (at once under reduced motion). `settled` lifts
  // the overflow clip once the open animation has finished, so the body's
  // card shadow is not cut off while it rests.
  const [bodyMounted, setBodyMounted] = React.useState(false);
  const [bodySettled, setBodySettled] = React.useState(false);
  React.useEffect(() => {
    if (expanded) {
      setBodyMounted(true);
      if (reducedMotion) setBodySettled(true);
      return undefined;
    }
    setBodySettled(false);
    if (reducedMotion) {
      setBodyMounted(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setBodyMounted(false), COLLAPSE_MS + 40);
    return () => window.clearTimeout(timer);
  }, [expanded, reducedMotion]);
  // The header keeps its attached chrome (square bottom corners, card
  // shadow) for as long as the body is there, closing included.
  const attached = expanded || bodyMounted;
  const primary = primaryColor ?? accentColor;

  const finishedCount = receipt.length;
  const inCampaignCount = receipt.filter((r) => r.inCampaign).length;
  const state = deriveDisclosureState(waymark, {
    allSettled,
    activeCommitted,
    finishedCount,
  });
  const stage = STAGE[state];

  // One glow pulse when the row FLIPS to ready (not on every render while
  // ready, and not on a refresh that lands already-ready). Tracked as a
  // transition from a non-ready state seen in this mount.
  const prevStateRef = React.useRef<DisclosureState | null>(null);
  const [glow, setGlow] = React.useState(false);
  React.useEffect(() => {
    const prev = prevStateRef.current;
    prevStateRef.current = state;
    if (state === 'ready' && prev != null && prev !== 'ready') {
      setGlow(true);
      const t = window.setTimeout(() => setGlow(false), 1500);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [state]);

  const toggle = React.useCallback(() => {
    setExpanded((e) => {
      if (!e) analytics.track(EVENTS.WAYMARK_BANNER_EXPANDED, { state });
      return !e;
    });
  }, [state]);

  const pill = pillFor(state, {
    onCustomize,
    onRun,
    // Make another is offered only once every commercial is committed; while
    // one is still uncommitted (the Customize-on-committed edge) the row
    // describes the campaign without offering a new row.
    onMakeAnother: allSettled ? onMakeAnother : undefined,
    isCommitting,
    isMakingAnother,
  });

  // Copy per state. Buyer-facing words only ("AI commercial", never "link"
  // or "generation").
  let lead: string;
  let teaser: string | null = null;
  switch (state) {
    // Wait and ready states carry the segmented stage indicator (with the
    // time estimate) instead of a progress teaser; the copy names the offer
    // as free wherever it is being offered.
    case 'analyzing':
    case 'drafting':
      lead = "Don't have a commercial? We're building a free one from your website.";
      break;
    case 'customize':
      lead = "Don't have a commercial? We just drafted one from your website. Click Review to customize.";
      break;
    case 'finalizing':
      lead = 'Almost there. We are uploading your commercial.';
      break;
    case 'ready':
      lead = 'Your commercial is ready.';
      break;
    case 'failed':
      lead = "Don't have a commercial? We can build one from your website, free.";
      teaser = "We couldn't build your commercial. Open to retry.";
      break;
    case 'committed':
      // CTV-785: finished commercials that left the campaign still count; the
      // lead says how many of them are in it right now.
      lead =
        inCampaignCount === finishedCount
          ? `${finishedCount} AI commercial${finishedCount === 1 ? '' : 's'} in your campaign.`
          : `${finishedCount} AI commercials, ${inCampaignCount} in your campaign.`;
      if (allSettled) lead += ' Want another?';
      teaser = allSettled
        ? "We'll make a fresh one from the same website, free."
        : 'Another commercial is in progress.';
      break;
    case 'idle':
    default:
      lead = "Don't have a commercial? We can build one from your website, free.";
  }

  // A faint dark scrim over the station gradient keeps the white text and the
  // white pill legible on light station palettes (KITV cyan) and is
  // invisible on dark ones (KEZI navy).
  const rowStyle: React.CSSProperties & Record<string, string> = {
    background: `linear-gradient(rgba(10, 18, 32, 0.14), rgba(10, 18, 32, 0.14)), linear-gradient(135deg, ${accentColor}, ${primary})`,
    color: '#ffffff',
    ['--wm-primary']: primary,
    ['--wm-glow']: tintFromAccent(accentColor, 0.45),
  };
  // The page's shared card elevation (steps/_shared accentVars --card-shadow),
  // so the expanded unit sits at the same depth as its sibling cards. Applied
  // inline on both halves while expanded; inline beats the CSS hover rule, so
  // hovering the expanded header cannot restyle it.
  const cardShadow = `0 1px 0 rgba(15,23,42,0.02), 0 14px 32px -16px ${tintFromAccent(accentColor, 0.4)}, 0 4px 12px -6px ${tintFromAccent(accentColor, 0.25)}`;
  if (attached) rowStyle.boxShadow = cardShadow;

  return (
    <div>
      <div
        data-testid="waymark-disclosure"
        data-state={state}
        data-glow={glow && !reducedMotion ? 'true' : 'false'}
        data-reduced-motion={reducedMotion ? 'true' : 'false'}
        data-expanded={expanded ? 'true' : 'false'}
        onClick={toggle}
        /* CTV-775 cohesion: expanded, the row is the HEADER of one continuous
           surface. Bottom corners square off and the hover lift stops (the
           header must not detach from its body). */
        className={`ctv-wm-row relative w-full text-left ${attached ? 'rounded-t-xl' : 'rounded-xl'} px-4 py-3 cursor-pointer overflow-hidden flex items-center gap-3`}
        style={rowStyle}
      >
        {!reducedMotion && (
          <span
            aria-hidden
            className="ctv-wm-sweep absolute inset-0 pointer-events-none"
            style={{
              transform: 'translateX(-100%)',
              background:
                'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.22) 50%, transparent 70%)',
            }}
          />
        )}

        {state === 'ready' && waymark?.renderedUrl && (
          <ReadyPoster renderedUrl={waymark.renderedUrl} />
        )}
        {state === 'committed' && finishedCount > 0 && (
          <div className="flex items-center shrink-0 relative" data-testid="waymark-banner-posters">
            {receipt.slice(0, 3).map((r, i) => (
              <SlotPoster
                key={r.commercialId}
                slug={slug}
                storagePath={r.storagePath}
                fallbackUrl={r.renderedUrl}
                index={i}
              />
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse AI commercial' : 'Expand AI commercial'}
          className="relative flex-1 min-w-0 text-left bg-transparent border-0 p-0 cursor-pointer text-white"
        >
          <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-white/85">
            <Sparkles className="w-3 h-3" aria-hidden />
            AI commercial
            {state === 'committed' && (
              <span
                className="ml-1 normal-case tracking-normal text-[11px] font-semibold rounded-full px-2 py-0.5"
                style={{ background: 'rgba(255,255,255,0.18)' }}
                data-testid="waymark-banner-count"
              >
                {inCampaignCount} in your campaign
              </span>
            )}
          </span>
          <span className="block text-[12.5px] leading-relaxed mt-1 text-white/95">{lead}</span>
          {stage.stage != null && (
            <span
              className="flex items-center gap-2 mt-1.5"
              data-testid="waymark-banner-stages"
              data-stage={stage.stage}
              aria-label={`Stage ${stage.stage} of 4: ${stage.label}`}
            >
              <span className="flex items-center gap-1" aria-hidden>
                {[1, 2, 3, 4].map((n) => {
                  const segState =
                    stage.done || n < stage.stage!
                      ? 'done'
                      : n === stage.stage
                        ? stage.pulse && !reducedMotion
                          ? 'active'
                          : 'current'
                        : 'pending';
                  return (
                    <span
                      key={n}
                      className="ctv-wm-seg block w-[18px] h-[6px] rounded-full"
                      data-state={segState}
                      style={{
                        background:
                          segState === 'pending' ? 'rgba(255,255,255,0.28)' : '#ffffff',
                        opacity: segState === 'current' ? 0.85 : 1,
                      }}
                    />
                  );
                })}
              </span>
              <span className="text-[12px] font-semibold text-white" data-testid="waymark-banner-stage-label">
                {stage.label}
                {stage.estimate && (
                  <span className="font-medium text-white/75"> · {stage.estimate}</span>
                )}
              </span>
            </span>
          )}
          {teaser && (
            <span
              className="block text-[12px] font-medium leading-relaxed mt-0.5 text-white"
              data-testid="waymark-banner-teaser"
            >
              {teaser}
            </span>
          )}
        </button>

        {pill && (
          <PrimaryButton
            type="button"
            variant="hero"
            inverse
            shimmer={pill.shimmer}
            size="sm"
            accentColor={accentColor}
            primaryColor={primary}
            disabled={pill.disabled}
            data-testid="waymark-banner-pill"
            data-action={pill.action}
            className="relative shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              analytics.track(EVENTS.WAYMARK_BANNER_PILL_CLICKED, { action: pill.action });
              pill.onClick();
            }}
          >
            {pill.icon}
            {pill.label}
          </PrimaryButton>
        )}

        <ChevronDown
          aria-hidden
          className={`relative w-4 h-4 shrink-0 text-white/75 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />

      </div>
      {bodyMounted && (
        // CTV-785: the unfold wrapper (see the effect above). Inside it, the
        // attached shell: flush against the banner (no gap), bordered on
        // three sides, rounded only at the bottom, and carrying the page's
        // shared card elevation. The panel states render borderless content
        // inside it.
        <div
          className="ctv-wm-expand"
          data-closing={expanded ? 'false' : 'true'}
          data-settled={bodySettled ? 'true' : 'false'}
          data-testid="waymark-disclosure-expand"
          onAnimationEnd={(e) => {
            if (e.target === e.currentTarget && expanded) setBodySettled(true);
          }}
        >
          <div className="ctv-wm-expand-clip">
            <div
              className="rounded-b-xl border border-t-0 border-slate-200 bg-white px-4 py-3.5"
              style={{ boxShadow: cardShadow }}
              data-testid="waymark-disclosure-body"
            >
              {waymark || allSettled ? (
                children
              ) : (
                <p className="text-[12px] text-slate-400">Connecting…</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface PillSpec {
  action: 'customize' | 'run' | 'make_another';
  label: string;
  icon: React.ReactNode;
  shimmer: 'always' | 'hover';
  disabled: boolean;
  onClick: () => void;
}

function pillFor(
  state: DisclosureState,
  h: {
    onCustomize?: () => void;
    onRun?: () => void;
    onMakeAnother?: () => void;
    isCommitting: boolean;
    isMakingAnother: boolean;
  },
): PillSpec | null {
  if (state === 'customize' && h.onCustomize) {
    return {
      action: 'customize',
      label: 'Review',
      icon: <Pencil className="w-3.5 h-3.5" aria-hidden />,
      shimmer: 'hover',
      disabled: false,
      onClick: h.onCustomize,
    };
  }
  if (state === 'ready' && h.onRun) {
    return {
      action: 'run',
      label: 'Run this commercial',
      icon: h.isCommitting ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
      ) : (
        <Sparkles className="w-3.5 h-3.5" aria-hidden />
      ),
      shimmer: 'always',
      disabled: h.isCommitting,
      onClick: h.onRun,
    };
  }
  if (state === 'committed' && h.onMakeAnother) {
    return {
      action: 'make_another',
      label: 'Make another commercial',
      icon: h.isMakingAnother ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
      ) : (
        <Plus className="w-3.5 h-3.5" aria-hidden />
      ),
      shimmer: 'hover',
      disabled: h.isMakingAnother,
      onClick: h.onMakeAnother,
    };
  }
  return null;
}

// Single poster for the ready state: the rendered file itself, seeked to
// 1.5s so the first frame is not black. Muted + playsInline keep mobile
// browsers from taking over the row.
const ReadyPoster: React.FC<{ renderedUrl: string }> = ({ renderedUrl }) => (
  <span
    className="relative w-[84px] h-[48px] rounded-lg overflow-hidden shrink-0 bg-black"
    style={{ boxShadow: '0 0 0 2px rgba(255,255,255,0.55), 0 8px 18px -8px rgba(0,0,0,0.6)' }}
    data-testid="waymark-banner-poster"
  >
    <video
      src={`${renderedUrl}#t=1.5`}
      preload="metadata"
      muted
      playsInline
      className="w-full h-full object-cover"
    />
    <span className="absolute inset-0 grid place-items-center text-white" aria-hidden>
      <span
        className="w-[22px] h-[22px] rounded-full grid place-items-center"
        style={{ background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(2px)' }}
      >
        <Play className="w-2.5 h-2.5" fill="currentColor" />
      </span>
    </span>
  </span>
);

// One stacked poster per finished commercial. Server-generated poster still
// when it exists, else the signed video seeked to 0.5s; CTV-785: a commercial
// no slot carries falls back to its Waymark render.
const SlotPoster: React.FC<{
  slug: string;
  storagePath: string | null;
  fallbackUrl: string | null;
  index: number;
}> = ({ slug, storagePath, fallbackUrl, index }) => {
  const { url: signedUrl, posterUrl } = useCreativePreviewUrl(slug, storagePath);
  const url = signedUrl ?? fallbackUrl;
  return (
    <span
      className="relative w-[56px] h-[34px] rounded-lg overflow-hidden shrink-0 border-2 border-white bg-black"
      style={{ marginLeft: index === 0 ? 0 : -14, zIndex: 3 - index }}
      data-testid="waymark-banner-slot-poster"
    >
      {posterUrl ? (
        <img src={posterUrl} alt="" className="w-full h-full object-cover" />
      ) : url ? (
        <video src={`${url}#t=0.5`} preload="metadata" muted playsInline className="w-full h-full object-cover" />
      ) : (
        <span
          className="block w-full h-full"
          style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.35), rgba(255,255,255,0.15))' }}
        />
      )}
    </span>
  );
};

export default WaymarkDisclosure;
